import { useEffect, useRef, useState } from 'react'

export const SCROLL_EDGE_TOLERANCE_PX = 2
export const SCROLL_RESET_DELAY_MS = 600
export const SCROLL_LOCK_DELAY_MS = 800
// environ 4 gestes de molette (4 crans x ~100 px)
export const SCROLL_THRESHOLD_PX = 400

const LINE_HEIGHT_PX = 16
const PROGRESS_STEPS = 20

export type ScrollNavigationDirection = 'prev' | 'next'

interface UseScrollNavigationParams {
  enabled: boolean
  canPrev: boolean
  canNext: boolean
  onPrev: () => void
  onNext: () => void
}

const normalizeDelta = (event: WheelEvent) => {
  if (event.deltaMode === 1) return event.deltaY * LINE_HEIGHT_PX
  if (event.deltaMode === 2) return event.deltaY * window.innerHeight
  return event.deltaY
}

const isReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const isAtEdge = (direction: ScrollNavigationDirection) => {
  const el = document.scrollingElement ?? document.documentElement
  const scrollTop = el.scrollTop || window.scrollY || 0
  if (direction === 'prev') return scrollTop <= SCROLL_EDGE_TOLERANCE_PX
  return scrollTop + window.innerHeight >= el.scrollHeight - SCROLL_EDGE_TOLERANCE_PX
}

// Un conteneur défilant (liste, zone de texte) peut encore absorber le geste
const canScrollInside = (target: EventTarget | null, direction: ScrollNavigationDirection) => {
  let node = target instanceof Element ? target : null
  while (node && node !== document.body && node !== document.documentElement) {
    const overflowY = window.getComputedStyle(node).overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      const atTop = node.scrollTop <= SCROLL_EDGE_TOLERANCE_PX
      const atBottom =
        node.scrollTop + node.clientHeight >= node.scrollHeight - SCROLL_EDGE_TOLERANCE_PX
      if (direction === 'prev' ? !atTop : !atBottom) return true
    }
    node = node.parentElement
  }
  return false
}

export default function useScrollNavigation({
  enabled,
  canPrev,
  canNext,
  onPrev,
  onNext,
}: UseScrollNavigationParams) {
  const [progress, setProgress] = useState(0)
  const [direction, setDirection] = useState<ScrollNavigationDirection | null>(null)

  // Valeurs lues via une ref pour ne pas réabonner le listener à chaque render
  const latestRef = useRef({ canPrev, canNext, onPrev, onNext })
  latestRef.current = { canPrev, canNext, onPrev, onNext }

  useEffect(() => {
    if (!enabled) return

    let accumulated = 0
    let currentDirection: ScrollNavigationDirection | null = null
    let gestureAccepted = false
    let lastEventAt = 0
    let lockedUntil = 0
    let resetTimer: ReturnType<typeof setTimeout> | undefined
    let shownProgress = 0

    const publish = (value: number, dir: ScrollNavigationDirection | null) => {
      const rounded = Math.round(Math.min(value, 1) * PROGRESS_STEPS) / PROGRESS_STEPS
      if (rounded !== shownProgress) {
        shownProgress = rounded
        setProgress(rounded)
      }
      setDirection(rounded === 0 ? null : dir)
    }

    const reset = () => {
      accumulated = 0
      currentDirection = null
      gestureAccepted = false
      publish(0, null)
    }

    const handleWheel = (event: WheelEvent) => {
      const now = Date.now()
      if (now < lockedUntil || isReducedMotion()) return

      const delta = normalizeDelta(event)
      if (delta === 0) return
      const eventDirection: ScrollNavigationDirection = delta < 0 ? 'prev' : 'next'

      const isNewGesture =
        currentDirection !== eventDirection || now - lastEventAt > SCROLL_RESET_DELAY_MS
      lastEventAt = now

      if (isNewGesture) {
        accumulated = 0
        currentDirection = eventDirection
        // la butée, le focus et la possibilité de naviguer sont vérifiés au début du geste
        const allowed =
          eventDirection === 'prev' ? latestRef.current.canPrev : latestRef.current.canNext
        gestureAccepted =
          allowed &&
          isAtEdge(eventDirection) &&
          (eventDirection === 'prev' || !canScrollInside(event.target, eventDirection))
      }

      clearTimeout(resetTimer)
      if (!gestureAccepted) return

      accumulated += Math.abs(delta)
      publish(accumulated / SCROLL_THRESHOLD_PX, eventDirection)

      if (accumulated >= SCROLL_THRESHOLD_PX) {
        lockedUntil = now + SCROLL_LOCK_DELAY_MS
        reset()
        if (eventDirection === 'prev') latestRef.current.onPrev()
        else latestRef.current.onNext()
        return
      }

      resetTimer = setTimeout(reset, SCROLL_RESET_DELAY_MS)
    }

    window.addEventListener('wheel', handleWheel, { passive: true })
    return () => {
      window.removeEventListener('wheel', handleWheel)
      clearTimeout(resetTimer)
      // évite un affichage résiduel de l'indicateur à la réactivation
      setProgress(0)
      setDirection(null)
    }
  }, [enabled])

  return { progress: enabled ? progress : 0, direction: enabled ? direction : null }
}
